import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Sparkles, Check, Workflow, Plug, FlaskConical, BarChart3, Users, Rocket } from 'lucide-react';
import './AgentStudio.css';

const features = [
  { icon: Workflow, title: 'Visual Flow Builder', desc: 'Drag-and-drop agent logic. No YAML, no code — just design the conversation and workflow.' },
  { icon: Plug, title: 'One-Click Integrations', desc: 'Connect WhatsApp, Telegram, Slack, HubSpot, Notion and 50+ tools instantly.' },
  { icon: FlaskConical, title: 'Live Test Console', desc: 'Test your agent against real inputs before deploying. Catch edge cases early.' },
  { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Every conversation, task, and failure in real time. Know what your agent is doing.' },
  { icon: Users, title: 'Team Collaboration', desc: 'Invite your team. Review, comment, and approve agent flows together.' },
  { icon: Rocket, title: 'One-Click Deploy', desc: 'Design to live in minutes. Deploy to any channel without touching a server.' },
];

const comparison = [
  ['Who it’s for', 'Enterprises & teams with complex needs', 'SMBs & developers who want self-serve'],
  ['How you start', 'Contact us → scoping call', 'Sign up → build immediately'],
  ['Time to deploy', '2–4 weeks', 'Hours'],
  ['Customization', 'Unlimited — fully bespoke', 'High — within platform'],
  ['Support', 'Dedicated team', 'Docs + community + paid tiers'],
];

export const AgentStudio = () => {
  const reduced = useReducedMotion();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
  };

  const fade = {
    hidden: { opacity: 0, y: reduced ? 0 : 22 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0.2 : 0.6, delay: reduced ? 0 : i * 0.08, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <div className="studio-page">
      {/* Hero */}
      <section className="studio-hero">
        <div className="studio-hero-glow" aria-hidden="true" />
        <motion.span className="studio-hero-chip" initial="hidden" animate="visible" variants={fade}>
          <Sparkles size={14} /> Coming Soon
        </motion.span>
        <motion.h1 className="studio-hero-title" initial="hidden" animate="visible" variants={fade} custom={1}>
          Agent Studio
        </motion.h1>
        <motion.p className="studio-hero-sub" initial="hidden" animate="visible" variants={fade} custom={2}>
          Build, test, and deploy AI agents — visually. No code required.
        </motion.p>
        <motion.p className="studio-hero-body" initial="hidden" animate="visible" variants={fade} custom={3}>
          Agent Studio is Liceron’s visual platform for building production AI agents. Built on the
          same infrastructure behind our enterprise deployments — now available for any team.
        </motion.p>

        <motion.form className="studio-hero-form" onSubmit={onSubmit} initial="hidden" animate="visible" variants={fade} custom={4}>
          {submitted ? (
            <p className="studio-hero-success"><Check size={18} /> You’re on the list. We’ll email you at launch.</p>
          ) : (
            <>
              <label htmlFor="studio-waitlist" className="visually-hidden">Email address</label>
              <input
                id="studio-waitlist"
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit">Join Waitlist</button>
            </>
          )}
        </motion.form>
        <motion.p className="studio-hero-note" initial="hidden" animate="visible" variants={fade} custom={5}>
          Be the first to know. We’ll email you when Agent Studio launches.
        </motion.p>
      </section>

      {/* Features */}
      <section className="studio-features-section">
        <h2 className="studio-section-title">What’s coming</h2>
        <div className="studio-features-grid">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                className="studio-feature-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                variants={fade}
                custom={i % 3}
              >
                <div className="studio-feature-icon"><Icon size={20} /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Comparison */}
      <section className="studio-compare-section">
        <h2 className="studio-section-title">Custom Build vs Agent Studio</h2>
        <div className="studio-table">
          <div className="studio-table-head">
            <span />
            <span className="studio-col-now">Custom Build <em>Now</em></span>
            <span className="studio-col-soon">Agent Studio <em>Soon</em></span>
          </div>
          {comparison.map(([label, now, soon]) => (
            <div className="studio-table-row" key={label}>
              <span className="studio-cell-label">{label}</span>
              <span className="studio-cell">{now}</span>
              <span className="studio-cell studio-cell-soon">{soon}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
